/**
 * TimelineViewport Component
 *
 * A single timeline row with label and canvas.
 * Wraps TimelineCanvas with label and gets zoom/pan state from context.
 */

import { type ReactNode } from 'react'
import { TimelineCanvas } from './TimelineCanvas'
import { useTimelineContext } from '../context/TimelineContext'
import type { TimelineViewportProps } from '../types'

interface ExtendedTimelineViewportProps extends Omit<TimelineViewportProps, 'label'> {
  /** Label can be a string or ReactNode for custom content */
  label?: string | ReactNode
  /** Timeline domain bounds (optional - uses context if not provided) */
  domain?: {
    minDate: number
    maxDate: number
  }
  /** Container width in pixels (optional - uses context if not provided) */
  containerWidth?: number
  /** Currently hovered segment UID (for bidirectional highlighting) */
  hoveredSegmentUid?: string | null
  /** Callback when segment hover state changes */
  onSegmentHover?: (uid: string | null) => void
}

/**
 * TimelineViewport Component
 */
export function TimelineViewport({
  segments,
  backgroundSegments,
  label,
  height,
  showBackgroundSegments,
  onSegmentClick,
  selectedSegmentUid,
  hoveredSegmentUid,
  onSegmentHover,
  domain: propDomain,
  containerWidth: propContainerWidth,
}: ExtendedTimelineViewportProps) {
  // Get zoom/pan state from context
  const { state, setTransform } = useTimelineContext()

  // Use props if provided, otherwise fall back to context values
  const domain = propDomain || state.domain
  const containerWidth = propContainerWidth || state.containerWidth

  return (
    <div className="timeline-viewport">
      {/* Row label */}
      {label && (
        <div className="timeline-viewport__label">
          {typeof label === 'string' ? <span>{label}</span> : label}
        </div>
      )}

      {/* Canvas container */}
      <div
        className="timeline-viewport__container"
        style={{ height }}
      >
        <TimelineCanvas
          segments={segments}
          backgroundSegments={backgroundSegments}
          height={height}
          showBackgroundSegments={showBackgroundSegments}
          onSegmentClick={onSegmentClick}
          selectedSegmentUid={selectedSegmentUid}
          hoveredSegmentUid={hoveredSegmentUid}
          onSegmentHover={onSegmentHover}
          domain={domain}
          containerWidth={containerWidth}
          transform={state.transform}
          constraints={state.constraints}
          onTransformChange={setTransform}
        />
      </div>
    </div>
  )
}

export default TimelineViewport
